import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, SimpleSelect } from '@/components/shared/ui';
import { User } from '../../../types/admin';
import { Input } from '@/components/shared/ui/input';
import { Building, User as UserIcon } from 'lucide-react';
import {
    CUSTOM_JOB_ROLE_VALUE,
    JOB_ROLE_TRANSLATION_KEYS,
    JOB_ROLE_VALUES,
    useEditUserForm,
} from './useEditUserForm';

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User | null;
    onSave: (user: User) => void | Promise<void>;
    onDelete?: (user: User) => void;
}

const readOnlyInputClassName = 'bg-gray-50';

export default function EditUserModal({ isOpen, onClose, user, onSave, onDelete }: EditUserModalProps): React.ReactElement {
    const { t } = useTranslation(['users', 'auth']);
    const {
        customJobRole,
        displayLicenseOrganization,
        displayRoleBadge,
        formData,
        handleCustomJobRoleChange,
        handleInputChange,
        handleJobRoleChange,
        handleSubmit,
        isGuest,
        isOrganizationCustomer,
        isSubmitting,
        jobRole,
        setFormData,
    } = useEditUserForm({ user, onSave, onClose });

    const footer = (
        <div className="flex w-full items-center justify-between gap-2">
            <div>
                {user && onDelete && (
                    <Button
                        type="button"
                        variant="outlineDanger"
                        onClick={() => {
                            onDelete(user);
                            onClose();
                        }}
                        disabled={isSubmitting}
                    >
                        {t('delete')}
                    </Button>
                )}
            </div>
            <div className="flex items-center gap-2">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    {t('editUserModal.cancel')}
                </Button>
                <Button
                    type="submit"
                    form="edit-user-form"
                    variant="primary"
                    disabled={isSubmitting}
                >
                    {t('editUserModal.save')}
                </Button>
            </div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('editUserModal.title')} footer={footer} size="large">
            <form id="edit-user-form" onSubmit={handleSubmit} className="space-y-5">
                {user && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between rounded-xl">
                            <div className="flex min-w-0 items-center gap-2">
                                {isOrganizationCustomer ? (
                                    <Building size={20} className="shrink-0 text-green-600" />
                                ) : (
                                    <UserIcon size={20} className="shrink-0 text-indigo-600" />
                                )}
                                <span className="truncate text-lg font-semibold text-gray-700">
                                    {displayLicenseOrganization}
                                </span>
                            </div>
                            <div className="shrink-0 px-4 py-1 border border-blue-500 text-blue-500 rounded-lg">
                                <span className="text-base font-semibold text-blue-700">
                                    {displayRoleBadge}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('tableUserId')}</label>
                    <Input
                        type="text"
                        name="loginId"
                        value={user?.loginId || user?.id || t('editUserModal.noLoginId')}
                        disabled={true}
                        readOnly
                        className={readOnlyInputClassName}
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('tableUserName')}</label>
                    <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        disabled={true}
                        readOnly
                        className={readOnlyInputClassName}
                    />
                </div>

                {isGuest && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {t('editUserModal.password')} <span className="text-sm text-gray-500 font-normal">{t('editUserModal.passwordHint')}</span>
                        </label>
                        <Input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                            placeholder={t('editUserModal.passwordPlaceholder')}
                        />
                    </div>
                )}

                {!isGuest && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{t('editUserModal.email')}</label>
                        <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            disabled={true}
                            readOnly
                            className={readOnlyInputClassName}
                        />
                    </div>
                )}

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('editUserModal.phoneNumber')}</label>
                    <Input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="01012345678"
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('editUserModal.country')}</label>
                    <Input
                        type="text"
                        name="country"
                        value={formData.country || '-'}
                        disabled={true}
                        readOnly
                        className={readOnlyInputClassName}
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('editUserModal.affiliationLabel')}</label>
                    <Input
                        type="text"
                            name="affiliation"
                            value={formData.affiliation}
                        onChange={handleInputChange}
                        placeholder={t('editUserModal.affiliationPlaceholder')}
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('editUserModal.position')}</label>
                    <Input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        placeholder={t('editUserModal.positionPlaceholder')}
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">{t('editUserModal.department')}</label>
                    <SimpleSelect
                        value={jobRole}
                        onChange={(e) => {
                            handleJobRoleChange(e.target.value);
                        }}
                    >
                        <option value="">{t('settings.jobRoleSelect', { ns: 'auth' })}</option>
                        {JOB_ROLE_VALUES.map((value) => (
                            <option key={value} value={value}>
                                {t(`settings.${JOB_ROLE_TRANSLATION_KEYS[value]}`, { ns: 'auth' })}
                            </option>
                        ))}
                        <option value={CUSTOM_JOB_ROLE_VALUE}>{t('settings.jobRoleCustom', { ns: 'auth' })}</option>
                    </SimpleSelect>
                </div>

                {jobRole === CUSTOM_JOB_ROLE_VALUE && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {t('settings.jobRoleCustomLabel', { ns: 'auth' })}
                        </label>
                        <Input
                            type="text"
                            value={customJobRole}
                            onChange={(e) => {
                                handleCustomJobRoleChange(e.target.value);
                            }}
                            placeholder={t('settings.jobRolePlaceholderCustom', { ns: 'auth' })}
                            className="border-gray-200 rounded-xl text-gray-900"
                        />
                    </div>
                )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {isGuest ? (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {t('editUserModal.expirationDate')} <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="date"
                                name="expirationDate"
                                value={formData.expirationDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
                                required
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {t('editUserModal.role')} <span className="text-red-500">*</span>
                            </label>
                            <SimpleSelect
                                name="role"
                                value={formData.role}
                                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                            >
                                <option value="Master">{t('roleMaster')}</option>
                                <option value="Student">{t('roleStudent')}</option>
                                <option value="Admin">{t('roleAdmin')}</option>
                            </SimpleSelect>
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {t('editUserModal.status')} <span className="text-red-500">*</span>
                        </label>
                        <SimpleSelect
                            name="status"
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        >
                            <option value="active">{t('editUserModal.statusActive')}</option>
                            <option value="inactive">{t('editUserModal.statusInactive')}</option>
                        </SimpleSelect>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
